// ============================================================================
// billingAccessModel.js
// Sprint 1 normalized entitlement/access contract for Process Advisor.
//
// This keeps the frontend from mixing three different concepts:
//   1. App-level access: Instance Subscription or Partner Client Slot
//   2. Model-level AI unlock: Assessment Credits
//   3. Per-process-model authorization marker
// ============================================================================

export const BILLING_ACCESS_MODEL_VERSION = 'sprint1-2026-05';
export const LIMITED_ACCESS_WORK_ITEM_LIMIT = 100;
export const ASSESSMENT_RECORD_LIMIT = 50000;

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const text = (value) => String(value ?? '').trim();
const lower = (value) => text(value).toLowerCase();

export const getRequiredAssessmentCredits = (recordCount = 0) => {
  const count = Math.max(1, toNumber(recordCount, 0));
  return Math.max(1, Math.ceil(count / ASSESSMENT_RECORD_LIMIT));
};

export const isFullAppAccess = (status = {}) => {
  const entitlementType = lower(status?.entitlementType || status?.platform || status?.billingAccess?.assessmentCredits?.credentialType || '');
  if (entitlementType === 'assessment_credits' || entitlementType === 'assessment-credit' || entitlementType === 'assessment credits') {
    return false;
  }
  const appAccess = status?.billingAccess?.appAccess || status?.appAccess || {};
  const subscriptionAccess = status?.billingAccess?.subscriptionAccess || status?.subscriptionAccess || {};
  const subscriptionSource = lower(appAccess.source || subscriptionAccess.source || '');
  const hasSubscriptionIdentifier = Boolean(
    status.serviceInstanceId || status.service_instance_id || status.subscriptionId || status.subscription_id || status.clientSlotId || status.client_slot_id || status.instanceUrl || status.instance_url ||
    subscriptionAccess.serviceInstanceId || subscriptionAccess.service_instance_id || subscriptionAccess.subscriptionId || subscriptionAccess.subscription_id || subscriptionAccess.clientSlotId || subscriptionAccess.client_slot_id || subscriptionAccess.instanceUrl || subscriptionAccess.instance_url
  );
  const isSubscriptionSource = (value) => ['subscription', 'instance_subscription', 'partner_subscription', 'partner_client_slot', 'dedicated_subscription'].includes(lower(value));
  if (appAccess.registered === true) return true;
  if (appAccess.mode === 'full' && (hasSubscriptionIdentifier || isSubscriptionSource(appAccess.source))) return true;
  if (subscriptionAccess.active === true && (hasSubscriptionIdentifier || isSubscriptionSource(subscriptionAccess.source || subscriptionAccess.entitlementType || subscriptionAccess.type))) return true;
  if ((lower(status.subscriptionStatus || status.subscription_status || subscriptionAccess.status) === 'active' || lower(status.subscriptionStatus || status.subscription_status || subscriptionAccess.status) === 'registered') && (hasSubscriptionIdentifier || isSubscriptionSource(subscriptionSource) || isSubscriptionSource(subscriptionAccess.source))) return true;
  return Boolean(
    status.hasSubscriptionAccess === true ||
    status.fullAppAccess === true ||
    status.annualAccess === true ||
    status.subscriptionAccess === true ||
    status.hasSubscription === true ||
    status.partnerAccess === true ||
    status.instanceRegistered === true ||
    status.clientSlotRegistered === true ||
    subscriptionSource === 'instance_subscription' ||
    subscriptionSource === 'partner_client_slot'
  );
};

export const getBillingAssessmentCreditBalance = (status = {}) => {
  const nested = status?.data || status?.result || {};
  const assessment = status?.billingAccess?.assessmentCredits || status?.assessmentCreditsAccess || status?.assessmentCredits || {};
  const value =
    assessment.creditBalance ??
    assessment.assessmentCredits ??
    status.creditBalance ??
    status.availableAssessmentCredits ??
    status.assessmentCredits ??
    status.credits ??
    status.available_credits ??
    status.assessment_credits ??
    status.credit_balance ??
    status.available_assessment_credits ??
    status.balance ??
    status.remainingCredits ??
    status.remaining_credits ??
    nested.creditBalance ??
    nested.availableAssessmentCredits ??
    nested.assessmentCredits ??
    nested.credits ??
    nested.available_credits ??
    nested.assessment_credits ??
    nested.credit_balance ??
    nested.available_assessment_credits ??
    nested.balance ??
    nested.remainingCredits ??
    nested.remaining_credits;
  return toNumber(value, 0);
};

export const normalizeBillingAccessStatus = (status = {}) => {
  const nested = status?.data || status?.result || {};
  const raw = { ...status, ...(nested || {}) };
  const availableAssessmentCredits = getBillingAssessmentCreditBalance(raw);
  const fullAccess = isFullAppAccess(raw);
  const existingBillingAccess = raw.billingAccess || {};

  const appAccess = {
    mode: fullAccess ? 'full' : (existingBillingAccess.appAccess?.mode || raw.appAccess?.mode || 'limited'),
    source: existingBillingAccess.appAccess?.source || raw.appAccess?.source || (
      raw.clientSlotRegistered || raw.partnerAccess ? 'partner_client_slot'
        : raw.instanceRegistered || raw.hasSubscription || raw.annualAccess ? 'instance_subscription'
          : 'none'
    ),
    registered: fullAccess,
    workItemLimit: fullAccess ? null : (existingBillingAccess.appAccess?.workItemLimit ?? raw.appAccess?.workItemLimit ?? LIMITED_ACCESS_WORK_ITEM_LIMIT),
    limitLabel: fullAccess ? 'Unlimited work items' : `${LIMITED_ACCESS_WORK_ITEM_LIMIT} work items in Evidence & Explorer`,
    reason: existingBillingAccess.appAccess?.reason || raw.appAccess?.reason || (fullAccess ? 'App-level access is registered.' : 'No app-level subscription is registered.'),
  };

  const subscriptionAccess = {
    ...(existingBillingAccess.subscriptionAccess || {}),
    ...(raw.subscriptionAccess && typeof raw.subscriptionAccess === 'object' ? raw.subscriptionAccess : {}),
    active: fullAccess,
    source: appAccess.source,
    serviceInstanceId: raw.serviceInstanceId ?? existingBillingAccess.subscriptionAccess?.serviceInstanceId ?? null,
    clientSlotId: raw.clientSlotId ?? existingBillingAccess.subscriptionAccess?.clientSlotId ?? null,
    instanceUrl: raw.instanceUrl ?? existingBillingAccess.subscriptionAccess?.instanceUrl ?? null,
    environment: raw.environment ?? existingBillingAccess.subscriptionAccess?.environment ?? null,
  };

  const assessmentCredits = {
    ...(existingBillingAccess.assessmentCredits || {}),
    configured: raw.configured === true || availableAssessmentCredits > 0 || existingBillingAccess.assessmentCredits?.configured === true,
    credentialType: 'assessment_credits',
    creditBalance: availableAssessmentCredits,
    assessmentCredits: availableAssessmentCredits,
    creditLimit: toNumber(raw.creditLimit ?? existingBillingAccess.assessmentCredits?.creditLimit, ASSESSMENT_RECORD_LIMIT),
    requiresDatasetAuthorization: !fullAccess,
  };

  return {
    ...raw,
    billingAccess: {
      entitlementModelVersion: raw.entitlementModelVersion || existingBillingAccess.entitlementModelVersion || BILLING_ACCESS_MODEL_VERSION,
      appAccess,
      assessmentCredits,
      subscriptionAccess,
      processModelAuthorization: raw.processModelAuthorization || existingBillingAccess.processModelAuthorization || null,
    },
    appAccess,
    subscriptionAccess,
    assessmentCreditsAccess: assessmentCredits,
    configured: raw.configured === true || raw.active === true || Boolean(raw.publicIntegrationKey || raw.customerId || raw.customer_id || nested.customerId || nested.customer_id),
    active: fullAccess || raw.active === true || availableAssessmentCredits > 0,
    availableAssessmentCredits,
    assessmentCredits: availableAssessmentCredits,
    creditBalance: availableAssessmentCredits,
    fullAppAccess: fullAccess,
  };
};
